import { sumBy } from 'lodash';
import { useMemo } from 'react';
import { PayCodeDto, TimesheetRowDto } from 'src/api/FiretrolApiClient';
import { TIMESHEET_DAY_KEYS } from 'src/app/timesheets/timesheetGrid';

interface TimesheetTotals {
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
  sun: number;
}

interface PayCodeHours {
  payCodeId: string;
  payCodeName: string;
  hours: number;
  sat: number;
  sun: number;
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
}

export const useTimesheetCalculations = (
  timesheetRows: TimesheetRowDto[],
  payCodes: PayCodeDto[],
) => {
  const totalHours = useMemo(() => {
    return sumBy(timesheetRows, function (o: TimesheetRowDto) {
      return Number(o.total);
    });
  }, [timesheetRows]);

  const hoursByPayCode = useMemo<PayCodeHours[]>(() => {
    const hoursMap = new Map<string, PayCodeHours>();

    payCodes.forEach((payCode) => {
      hoursMap.set(payCode.id, {
        payCodeId: payCode.id,
        payCodeName: payCode.name,
        hours: 0,
        sat: 0,
        sun: 0,
        mon: 0,
        tue: 0,
        wed: 0,
        thu: 0,
        fri: 0,
      });
    });

    timesheetRows.forEach((row) => {
      const entry = row.payCodeId ? hoursMap.get(row.payCodeId) : undefined;
      if (!entry) return;

      entry.hours += Number(row.total);
      TIMESHEET_DAY_KEYS.forEach((day) => {
        entry[day] += Number(row[day]);
      });
    });

    return payCodes.map((payCode) => hoursMap.get(payCode.id)!);
  }, [timesheetRows, payCodes]);

  const totalsByDay = useMemo<TimesheetTotals>(() => {
    const mon = sumBy(timesheetRows, function (o: TimesheetRowDto) {
      return Number(o.mon);
    });
    const tue = sumBy(timesheetRows, function (o: TimesheetRowDto) {
      return Number(o.tue);
    });
    const wed = sumBy(timesheetRows, function (o: TimesheetRowDto) {
      return Number(o.wed);
    });
    const thu = sumBy(timesheetRows, function (o: TimesheetRowDto) {
      return Number(o.thu);
    });
    const fri = sumBy(timesheetRows, function (o: TimesheetRowDto) {
      return Number(o.fri);
    });
    const sat = sumBy(timesheetRows, function (o: TimesheetRowDto) {
      return Number(o.sat);
    });
    const sun = sumBy(timesheetRows, function (o: TimesheetRowDto) {
      return Number(o.sun);
    });

    return {
      mon,
      tue,
      wed,
      thu,
      fri,
      sat,
      sun,
    };
  }, [timesheetRows]);

  // Only sum hours from rows on per-diem-eligible WBS accounts
  const perDiemEligibleTotalsByDay = useMemo<TimesheetTotals>(() => {
    const perDiemEligibleRows = timesheetRows.filter(
      (r) => r.wbsAccount?.perDiemEligible === true,
    );

    return {
      mon: sumBy(perDiemEligibleRows, (o: TimesheetRowDto) => Number(o.mon)),
      tue: sumBy(perDiemEligibleRows, (o: TimesheetRowDto) => Number(o.tue)),
      wed: sumBy(perDiemEligibleRows, (o: TimesheetRowDto) => Number(o.wed)),
      thu: sumBy(perDiemEligibleRows, (o: TimesheetRowDto) => Number(o.thu)),
      fri: sumBy(perDiemEligibleRows, (o: TimesheetRowDto) => Number(o.fri)),
      sat: sumBy(perDiemEligibleRows, (o: TimesheetRowDto) => Number(o.sat)),
      sun: sumBy(perDiemEligibleRows, (o: TimesheetRowDto) => Number(o.sun)),
    };
  }, [timesheetRows]);

  const getPayCodeName = useMemo(() => {
    return (payCodeId: string | undefined): string => {
      if (!payCodeId) return 'N/A';
      const payCode = payCodes.find((pc) => pc.id === payCodeId);
      return payCode?.name || 'N/A';
    };
  }, [payCodes]);

  return {
    totalHours,
    hoursByPayCode,
    totalsByDay,
    perDiemEligibleTotalsByDay,
    getPayCodeName,
  };
};
